import { useTranslation } from "react-i18next"

import { IconContainer } from "@/components/ui/node-pill"
import { cn } from "@/lib/utils"

type PrismPanelHeaderProps = {
  subtitle: string
  /**
   * The other state lines a live panel can show before it settles. The line
   * holds the room of the widest and tallest of them from mount, so a state
   * change never moves the badge or the content below.
   */
  reserve?: readonly [string, string]
  badge: string
  /** The badge reports grounded evidence rather than connections. */
  badgeDone: boolean
}

/** The Prism mark, title, current state line and count badge of a panel. */
export function PrismPanelHeader({ subtitle, reserve, badge, badgeDone }: PrismPanelHeaderProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2.5">
        <IconContainer active aria-hidden="true" size={30}>
          <svg
            fill="none"
            height="14"
            stroke="currentColor"
            strokeLinejoin="round"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
            width="14"
          >
            <path d="M12 3l9 16H3z" />
          </svg>
        </IconContainer>
        <div>
          <h4 className="type-headline text-[13px] leading-none">
            {t("activity_messages.filth.title", { defaultValue: "Prism" })}
          </h4>
          <p className="text-muted-foreground mt-1 text-xs">
            {reserve ? (
              <span
                className="prism-reserve"
                data-reserve-a={reserve[0]}
                data-reserve-b={reserve[1]}
              >
                <span data-testid="prism-panel-status">{subtitle}</span>
              </span>
            ) : (
              <span data-testid="prism-panel-status">{subtitle}</span>
            )}
          </p>
        </div>
      </div>
      <span
        data-testid="prism-badge"
        className={cn(
          "rounded-full border px-2.5 py-0.5 text-xs font-semibold tabular-nums",
          badgeDone ? "border-foreground bg-primary text-primary-foreground" : "border-border"
        )}
      >
        {badge}
      </span>
    </div>
  )
}
